import { useEffect, useRef, useState } from 'react';
import styles from './editor.module.css';
import Sk from "skulpt";
import "skulpt/dist/skulpt-stdlib.js";
import AceEditor from "react-ace";
import { transformPythonError } from '../utils/friendlyErrors';

// ── pygame-ce runs on the MAIN THREAD, not the worker ───────────────────────
// Reason: pygame-ce needs a real <canvas> plus DOM keyboard/mouse events.
// The regular Pyodide worker has no `document`, so SDL input can't attach
// there even with an OffscreenCanvas. Turtle already gets this special
// treatment via Skulpt; pygame gets the same treatment via its own
// lazily-loaded Pyodide instance.
const PYODIDE_CDN_BASE = "https://cdn.jsdelivr.net/pyodide/v0.27.7/full/";
// Once you're serving Pyodide same-origin (per your Vite migration), swap
// PYODIDE_CDN_BASE for "/pyodide/" and drop the CDN <script> loader below.

function loadScriptOnce(src) {
    return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
            resolve();
            return;
        }
        const s = document.createElement("script");
        s.src = src;
        s.onload = () => resolve();
        s.onerror = () => reject(new Error(`Failed to load ${src}`));
        document.head.appendChild(s);
    });
}

export default function Pyodide({
    handleIsLoading,
    pythonCode,
    output,
    handleOutput,
    onRun,
    OnRunForModalOption,
    editorValue
}) {
    const workerRef = useRef(null);
    const pendingRunRef = useRef(null);
    const runIdRef = useRef(0);
    const hardTimeoutRef = useRef(null);
    const [load, setLoad] = useState(false);
    const [plots, setPlots] = useState([]);
    const [isRunning, setIsRunning] = useState(false);
    const [isRestarting, setIsRestarting] = useState(false);

    // ── pygame state ─────────────────────────────────────────────────────
    const pygamePyodideRef = useRef(null);
    const pygameLoadingPromiseRef = useRef(null);
    const [pygameVisible, setPygameVisible] = useState(false);
    const [pygameLoading, setPygameLoading] = useState(false);
    const [pygameRunKey, setPygameRunKey] = useState(0);
    const pygameGenRef = useRef(0);
const isStartingPygameRef = useRef(false);


    const closingTurtle = () => {
        setLoad(false);
        const el = document.getElementById("turtle-container");
        if (el) el.innerHTML = "";
    };

    const pygameTaskRef = useRef(null);
    // Accumulates print() output from the pygame interpreter across a run.
    // handleOutput() replaces the whole Output panel value (it's not an
    // append API), so we keep the running total here and re-send it in
    // full every time new text arrives.
    const pygameOutputBufferRef = useRef("");

   const closingPygame = () => {
    const canvas = document.getElementById("canvas");
    if (canvas) canvas.blur();

    const aceEditor = document.querySelector(".ace_text-input");
    if (aceEditor) aceEditor.focus();

    setPygameVisible(false);
    setPygameFocused(false);

    // CRITICAL: force React to unmount the canvas on next run so any
    // zombie loop renders to a detached node instead of the visible one
    setPygameRunKey(prev => prev + 1);
    // 🔇 Prevent zombie prints from reaching the output panel
    window.pygameStdoutWrite = () => {};

    if (pygameTaskRef.current) {
        try { pygameTaskRef.current.cancel(); } catch (e) {}
        pygameTaskRef.current = null;
    }
    if (pygamePyodideRef.current) {
        try { pygamePyodideRef.current.destroy?.(); } catch (e) {}
        pygamePyodideRef.current = null;
    }
};

    function clearHardTimeout() {
        if (hardTimeoutRef.current) {
            clearTimeout(hardTimeoutRef.current);
            hardTimeoutRef.current = null;
        }
    }

    // ── Attach onmessage to a worker instance ─────────────────────────────
    // Called for both the initial worker and any restarted worker.
    function bindWorkerHandlers(worker) {
        worker.onmessage = function (event) {
            const { type, id, output, error, prompt, imageData } = event.data;

            if (type === "ready") {
                // console.log("✅ Pyodide worker ready");
                handleIsLoading();
                setIsRestarting(false);
                return;
            }



            if (type === "input_request") {
                showInputBox(
                    prompt || "Enter input:",
                    function (value) {
                        worker.postMessage({ type: "input_response", inputValue: value });
                    },
                    function () {
                        worker.postMessage({ type: "input_cancelled" });
                    }
                );
                return;
            }

            if (type === "plot") {
                setPlots(prev => [...prev, imageData]);
                return;
            }

            // Worker-side async timeout fired (slow sleeps, network calls, etc.)
            if (type === "timeout") {
                clearHardTimeout();
                setIsRunning(false);
                if (pendingRunRef.current?.id === id) {
                    pendingRunRef.current.resolve(
                        (event.data.output ? event.data.output + "\n" : "") +
                        "⏱ Your code took too long and was stopped (10s limit)."
                    );
                    pendingRunRef.current = null;
                }
                setIsRestarting(true);
                spawnNewWorker();
                return;
            }

            if (type === "result") {
                clearHardTimeout();
                setIsRunning(false);
                if (pendingRunRef.current?.id === id) {
                    pendingRunRef.current.resolve(output || "");
                    pendingRunRef.current = null;
                }
                return;
            }

            if (type === "error") {
                clearHardTimeout();
                setIsRunning(false);
                if (pendingRunRef.current?.id === id) {
                    pendingRunRef.current.resolve(
                        transformPythonError(error || "") + (output ? "\n" + output : "")
                    );
                    pendingRunRef.current = null;
                }
                return;
            }
        };

        worker.onerror = function (err) {
            console.error("Worker error:", err);
        };
    }

    // ── Create a fresh worker and attach handlers ──────────────────────────
    function spawnNewWorker() {
        if (workerRef.current) {
            workerRef.current.terminate();
        }
        const worker = new Worker("/pyodide.worker.js");
        workerRef.current = worker;
        bindWorkerHandlers(worker);
    }

    // ── Stop button handler ────────────────────────────────────────────────
    // Terminates the worker (kills any running Python, including sync loops),
    // resolves the pending promise so the UI updates immediately, then
    // recreates a fresh worker in the background.
    function stopExecution() {
        clearHardTimeout();
        setIsRunning(false);
        if (pendingRunRef.current) {
            pendingRunRef.current.resolve("🛑 Stopped.");
            pendingRunRef.current = null;
        }
        setIsRestarting(true);
        spawnNewWorker();
    }

    useEffect(() => {
        spawnNewWorker();
        onRun.current = onClickRun;
        OnRunForModalOption.current = onClickRunForModal;
        return () => {
            clearHardTimeout();
            if (workerRef.current) workerRef.current.terminate();
            onRun.current = null;
            OnRunForModalOption.current = null;
        };
    }, []);

    // ── Input box UI ──────────────────────────────────────────────────────
    function showInputBox(promptText, onSubmit, onCancel) {
        var existing = document.getElementById("py_input_overlay");
        if (existing) document.body.removeChild(existing);

        var overlay = document.createElement("div");
        overlay.id = "py_input_overlay";
        overlay.style.cssText = `
            position: fixed; top: 0; left: 0;
            width: 100%; height: 100%;
            background: rgba(0,0,0,0.6); z-index: 9999;
            display: flex; align-items: center; justify-content: center;
        `;

        var box = document.createElement("div");
        box.style.cssText = `
            background: #1e1e2e; border: 2px solid #4CAF50;
            border-radius: 10px; padding: 24px; min-width: 320px;
            box-shadow: 0 8px 32px rgba(0,0,0,0.6); font-family: monospace;
        `;

        var header = document.createElement("div");
        header.style.cssText = `
            display: flex; justify-content: space-between;
            align-items: center; margin-bottom: 12px;
        `;

        var label = document.createElement("div");
        label.innerText = promptText;
        label.style.cssText = `color: #4CAF50; font-size: 15px; flex: 1;`;

        var cancelX = document.createElement("button");
        cancelX.innerText = "✕";
        cancelX.title = "Cancel";
        cancelX.style.cssText = `
            background: none; border: none; color: #888;
            font-size: 18px; cursor: pointer; padding: 0 0 0 8px; line-height: 1;
        `;
        cancelX.onmouseenter = () => cancelX.style.color = "#ff5555";
        cancelX.onmouseleave = () => cancelX.style.color = "#888";
        cancelX.onclick = () => { removeOverlay(); onCancel(); };

        header.appendChild(label);
        header.appendChild(cancelX);

        var inputEl = document.createElement("input");
        inputEl.type = "text";
        inputEl.style.cssText = `
            width: 100%; padding: 8px 12px; background: #2d2d3f; color: white;
            border: 1px solid #555; border-radius: 6px; font-size: 15px;
            box-sizing: border-box; outline: none; margin-bottom: 14px;
        `;
        inputEl.placeholder = "Type here and press Enter...";

        var btnRow = document.createElement("div");
        btnRow.style.cssText = `display: flex; gap: 10px;`;

        var cancelBtn = document.createElement("button");
        cancelBtn.innerText = "Cancel";
        cancelBtn.style.cssText = `
            flex: 1; padding: 8px; background: transparent; color: #888;
            border: 1px solid #555; border-radius: 6px;
            font-size: 14px; cursor: pointer;
        `;
        cancelBtn.onmouseenter = () => {
            cancelBtn.style.color = "#ff5555";
            cancelBtn.style.borderColor = "#ff5555";
        };
        cancelBtn.onmouseleave = () => {
            cancelBtn.style.color = "#888";
            cancelBtn.style.borderColor = "#555";
        };
        cancelBtn.onclick = () => { removeOverlay(); onCancel(); };

        var okBtn = document.createElement("button");
        okBtn.innerText = "OK";
        okBtn.style.cssText = `
            flex: 1; padding: 8px; background: #4CAF50; color: white;
            border: none; border-radius: 6px; font-size: 14px; cursor: pointer;
        `;

        function removeOverlay() {
            var ov = document.getElementById("py_input_overlay");
            if (ov) document.body.removeChild(ov);
        }

        function submit() {
            removeOverlay();
            onSubmit(inputEl.value);
        }

        okBtn.onclick = submit;
        inputEl.onkeydown = (e) => {
            if (e.key === "Enter") submit();
            if (e.key === "Escape") { removeOverlay(); onCancel(); }
        };

        btnRow.appendChild(cancelBtn);
        btnRow.appendChild(okBtn);
        box.appendChild(header);
        box.appendChild(inputEl);
        box.appendChild(btnRow);
        overlay.appendChild(box);
        document.body.appendChild(overlay);
        setTimeout(() => inputEl.focus(), 50);
    }

    // ── Send code to worker, get output as Promise ────────────────────────
    async function runWithSkulpt(code) {
        const container = document.getElementById("turtle-container");
        if (!container) throw new Error("turtle-container div not found");
        container.innerHTML = "";

        let outputBuffer = "";

        Sk.configure({
            output: (text) => { outputBuffer += text; },
            // ✅ Async inputfun — uses the same overlay as Pyodide
            inputfun: (prompt) => new Promise((resolve) => {
                showInputBox(prompt || "Enter input:", resolve, () => resolve(""));
            }),
            read: (x) => {
                if (!Sk.builtinFiles?.["files"]?.[x]) {
                    throw new Error(`Skulpt: file not found: ${x}`);
                }
                return Sk.builtinFiles["files"][x];
            },
        });

        Sk.TurtleGraphics = {
            target: "turtle-container",
            width: 500,
            height: 400,
            mode: "standard",
        };

        await Sk.misceval.asyncToPromise(() =>
            Sk.importMainWithBody("<stdin>", false, code, true)
        );

        return outputBuffer;
    }


    // Near your other useState hooks
const [pygameFocused, setPygameFocused] = useState(false);

// Focus management effect
useEffect(() => {
    if (!pygameVisible) return;

    const handleMouseDown = (e) => {
        const container = document.getElementById("pygame-container");
        const canvas = document.getElementById("canvas");
        const aceInput = document.querySelector(".ace_text-input");
        if (!container || !canvas) return;

        if (container.contains(e.target)) {
            // Clicked inside the game — give it keyboard focus
            canvas.focus();
            setPygameFocused(true);
        } else {
            // Clicked outside — return focus to the code editor
            canvas.blur();
            setPygameFocused(false);
            if (aceInput) aceInput.focus();
        }
    };

    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
}, [pygameVisible]);

    // ── pygame-ce: fresh main-thread Pyodide instance PER RUN ─────────────
    // Not cached across runs on purpose — see the comment in runWithPygame.
    // pygame-ce's SDL state does not reliably survive teardown/reinit
    // within one interpreter, so each run gets its own clean one. The
    // pygame-ce wheel itself is still served from the browser's HTTP cache
    // after the first load, so this isn't a full re-download each time.
    async function getPygamePyodide() {
        // Guard against double-loading if the user mashes Run.
        if (pygameLoadingPromiseRef.current) return pygameLoadingPromiseRef.current;

        // Tear down whatever instance is currently sitting around before
        // creating the next one.
        if (pygamePyodideRef.current) {
            try { pygamePyodideRef.current.destroy?.(); } catch (e) {}
            pygamePyodideRef.current = null;
        }

        pygameLoadingPromiseRef.current = (async () => {
            if (!window.loadPyodide) {
                await loadScriptOnce(`${PYODIDE_CDN_BASE}pyodide.js`);
            }
            const pyo = await window.loadPyodide({ indexURL: PYODIDE_CDN_BASE });
            await pyo.loadPackage("pygame-ce");
            pygamePyodideRef.current = pyo;
            return pyo;
        })();

        try {
            return await pygameLoadingPromiseRef.current;
        } finally {
            pygameLoadingPromiseRef.current = null;
        }
    }

    // ── Make ordinary synchronous pygame loops cooperative ────────────────
    // pygame runs on the MAIN thread here (see comment above), so a plain
    // `while running: ... clock.tick(60)` loop never yields to the browser
    // and freezes the whole tab — there's no worker to terminate as a
    // safety net like your regular code execution has.
    //
    // Fix: wrap the script in an async function, and after every line that
    // calls `<something>.tick(...)` (the standard pygame idiom for frame
    // pacing), inject an `await __pygame_yield__()` yield. This lets
    // students write completely normal pygame code with no asyncio
    // knowledge required.
    // Reuses the same trick your worker's transformCode already applies:
    // asyncio.run(expr) → await expr. Needed here because we wrap every
    // script in our own outer async function — if student code also calls
    // asyncio.run(...), that spins up a SECOND, disconnected event loop,
    // which detaches pygame's SDL video/canvas state from the loop that's
    // actually driving the browser frame pump. Symptom: intermittent
    // "pygame.error: video system not initialized" right after the nested
    // loop takes over.
    function replaceAsyncioRun(code) {
        let result = "";
        let i = 0;
        const needle = "asyncio.run(";

        while (i < code.length) {
            const match = code.indexOf(needle, i);
            if (match === -1) {
                result += code.slice(i);
                break;
            }
            let depth = 1;
            let j = match + needle.length;
            while (j < code.length && depth > 0) {
                if (code[j] === "(") depth++;
                else if (code[j] === ")") depth--;
                j++;
            }
            const inner = code.slice(match + needle.length, j - 1);
            result += code.slice(i, match);
            result += `await ${inner}`;
            i = j;
        }
        return result;
    }

    // Injects `await __pygame_yield__()` as the last statement of every
    // top-level `while` loop body, regardless of whether the loop calls
    // clock.tick(). Loops that skip clock.tick() entirely (e.g. simple
    // event-polling loops) previously never got a yield point and froze
    // the tab exactly like a real infinite loop would.
    // Note: tracks one while-loop at a time, so it targets the outermost
    // loop correctly for typical single-loop game scripts; deeply nested
    // while loops beyond the first aren't individually instrumented.
    function injectLoopYields(code) {
        const lines = code.split("\n");
        const out = [];
        let loopIndent = null;
        let bodyIndent = null;

        const indentOf = (line) => line.match(/^(\s*)/)[1].length;
        const isBlank = (line) => line.trim() === "";

        for (const line of lines) {
            if (loopIndent !== null && bodyIndent !== null && !isBlank(line)) {
                if (indentOf(line) <= loopIndent) {
                    out.push(" ".repeat(bodyIndent) + "await __pygame_yield__()");
                    loopIndent = null;
                    bodyIndent = null;
                }
            }

            out.push(line);

            const whileMatch = line.match(/^(\s*)while\b.*:\s*$/);
            if (whileMatch) {
                loopIndent = whileMatch[1].length;
                bodyIndent = null;
                continue;
            }

            if (loopIndent !== null && bodyIndent === null && !isBlank(line)) {
                bodyIndent = indentOf(line);
            }
        }

        // Loop body ran to end of file (very common — `pygame.quit()` after
        // the loop is the only thing that follows, at lower indentation, so
        // this usually IS hit; but guard the EOF case too).
        if (loopIndent !== null && bodyIndent !== null) {
            out.push(" ".repeat(bodyIndent) + "await __pygame_yield__()");
        }

        return out.join("\n");
    }

    // Finds names assigned at the TOP LEVEL of the original script (before
    // wrapping), e.g. `game_over = False`, `player_x = 100`, `x, y = 1, 2`.
    // Skips indented lines, comments, and control-flow/def/import lines.
    function collectTopLevelAssignedNames(code) {
        const names = new Set();
        const assignOp = /^([A-Za-z_][A-Za-z0-9_]*(?:\s*,\s*[A-Za-z_][A-Za-z0-9_]*)*)\s*(?:=|\+=|-=|\*=|\/=|\/\/=|%=|\*\*=|&=|\|=|\^=|>>=|<<=)(?!=)/;
        for (const rawLine of code.split("\n")) {
            if (/^\s/.test(rawLine)) continue; // not top-level
            const line = rawLine.trim();
            if (!line || line.startsWith("#")) continue;
            if (/^(def|class|import|from|if|elif|else|for|while|try|except|finally|with|async)\b/.test(line)) continue;
            const m = line.match(assignOp);
            if (m) {
                m[1].split(",").forEach(n => names.add(n.trim()));
            }
        }
        return Array.from(names);
    }

    function transformPygameCode(rawCode) {
        const noNestedRun = replaceAsyncioRun(rawCode);
        const withYields = injectLoopYields(noNestedRun);
        const indented = withYields.split("\n").map(l => "    " + l).join("\n");

        // Without this, any nested `def` in the script (like a reset_game()
        // helper) that uses `global someVar` refers to a disconnected copy
        // in the true module namespace, NOT the local variable this wrapper
        // function's own loop actually reads — so state resets silently
        // fail to have any visible effect (e.g. pressing "R" after Game
        // Over does nothing, because reset_game() resets the wrong copy).
        const topLevelNames = collectTopLevelAssignedNames(rawCode);
        const globalDecl = topLevelNames.length > 0
            ? `    global ${topLevelNames.join(", ")}\n`
            : "";

        // Deliberately does NOT call __pygame_main__() itself — the caller
        // schedules it as a cancellable asyncio task instead, so a later run
        // (or the close button) can stop it cleanly rather than leaving two
        // loops fighting over the same global `pygame` module state.
        return `async def __pygame_main__():\n${globalDecl}${indented}`;
    }

   async function runWithPygame(code) {
    // Prevent double-clicking Run from starting two overlapping games
    if (isStartingPygameRef.current) return "";
    isStartingPygameRef.current = true;

    try {
        // If a game is already running, close it first and let React flush
        if (pygameVisible || pygamePyodideRef.current) {
            closingPygame();
            await new Promise(r => setTimeout(r, 300));
        }

        setPygameVisible(true);
        setPygameLoading(true);

        // Let React mount the new canvas before SDL binds to it
        await new Promise(requestAnimationFrame);
        await new Promise(requestAnimationFrame);

        const pyo = await getPygamePyodide();
        pygameTaskRef.current = null;

        const canvas = document.getElementById("canvas");
        if (!canvas) throw new Error("canvas element not found");
        pyo.canvas.setCanvas2D(canvas);

        // ── Wire up print()/stderr so they actually reach the Output panel ──
        // Without this, print() in pygame scripts silently goes to the
        // browser DevTools console instead — this is a separate main-thread
        // Pyodide instance from the worker, which is the only place that
        // normally captures stdout for you.
        pygameOutputBufferRef.current = "";
        handleOutput("");
        window.pygameStdoutWrite = (s) => {
            pygameOutputBufferRef.current += s;
            handleOutput(pygameOutputBufferRef.current);
        };
        await pyo.runPythonAsync(`
import sys, js as _js_stdout_bridge

class _JSStream:
    def write(self, s):
        _js_stdout_bridge.window.pygameStdoutWrite(s)
        return len(s)
    def flush(self):
        pass

sys.stdout = _JSStream()
sys.stderr = _JSStream()
`);

        await pyo.runPythonAsync(
`import os
os.environ['SDL_EMSCRIPTEN_KEYBOARD_ELEMENT'] = '#canvas'
import asyncio as _asyncio_internal
async def __pygame_yield__():
    await _asyncio_internal.sleep(0)`
        );

        setPygameLoading(false);
        if (canvas) canvas.focus();
        const defCode = transformPygameCode(code);
        await pyo.runPythonAsync(defCode); // registers __pygame_main__

        // Generation stamp so old runs can't close newer ones
        const gen = ++pygameGenRef.current;
        window.__onPygameDone = () => {
            if (pygameGenRef.current !== gen) return; // stale — ignore
            closingPygame();
        };

               pyo.runPython(`
import asyncio as _a
import js as _js

async def _wrapped_main():
    try:
        await __pygame_main__()
    finally:
        try:
            import pygame as _pg
            _pg.quit()
        except Exception:
            pass
        _js.window.__onPygameDone()

_task = _a.ensure_future(_wrapped_main())
`);

        const task = pyo.globals.get('_task');
        pygameTaskRef.current = task;

        return "";
    } catch (err) {
        setPygameLoading(false);
        const msg = String(err);
        if (msg.includes("SystemExit")) return "";
        throw err;
    } finally {
        isStartingPygameRef.current = false;
    }
}

    function runInWorker(code) {

        setPlots([]);
        setIsRunning(true);
        return new Promise((resolve, reject) => {
            const id = ++runIdRef.current;
            pendingRunRef.current = { id, resolve, reject };
            workerRef.current.postMessage({ type: "run", id, code });

            // Hard 10-second timeout: terminate worker if no response arrives.
            // This is the only mechanism that catches synchronous infinite loops
            // (e.g. while True: x+=1) which block the worker's JS event loop and
            // prevent the worker-side timeout from firing.
            hardTimeoutRef.current = setTimeout(() => {
                hardTimeoutRef.current = null;
                if (pendingRunRef.current?.id === id) {
                    setIsRunning(false);
                    pendingRunRef.current.resolve("⏱ Your code took too long and was stopped (60s limit).");
                    pendingRunRef.current = null;
                }
                setIsRestarting(true);
                spawnNewWorker();
            }, 60000);
        });
    }

    async function onClickRun() {
        const val = AceEditor.value;
        if (/\bimport\s+pygame\b/.test(val)) {
            setPlots([]);
            closingTurtle();
            try { handleOutput(await runWithPygame(val)); }
            catch (err) { handleOutput(transformPythonError(String(err))); }
            return;
        }
        if (/\bimport\s+turtle\b/.test(val)) {
            setPlots([]);
            closingPygame();
            setLoad(true);
            try { handleOutput(await runWithSkulpt(val)); }
            catch (err) { handleOutput(String(err)); }
            return;
        }
        handleOutput(await runInWorker(val));
    }

    async function onClickRunForModal(val) {
        if (!val) return;

        if (/\bimport\s+pygame\b/.test(val)) {
            setPlots([]);
            closingTurtle();
            try { handleOutput(await runWithPygame(val)); }
            catch (err) { handleOutput(transformPythonError(String(err))); }
            return;
        }
        if (/\bimport\s+turtle\b/.test(val)) {
            setPlots([]);
            closingPygame()
            setLoad(true);
            try { handleOutput(await runWithSkulpt(val)); }
            catch (err) { handleOutput(String(err)); }
            return;
        }
        handleOutput(await runInWorker(val));
    }

    return (
        <>
            {/* Running / Restarting status bar ── shown at top of output panel */}
            {(isRunning || isRestarting) && (
                <div style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    background: isRestarting ? "#555" : "#3e46ff",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "5px 10px",
                    zIndex: 20,
                    boxSizing: "border-box",
                    fontSize: "13px",
                    fontFamily: "monospace"
                }}>
                    <span>
                        {isRestarting ? "⟳  Restarting Python..." : "● Running..."}
                    </span>
                    {isRunning && !isRestarting && (
                        <button
                            onClick={stopExecution}
                            style={{
                                background: "#c0392b",
                                color: "white",
                                border: "none",
                                borderRadius: "4px",
                                padding: "2px 12px",
                                cursor: "pointer",
                                fontWeight: "bold",
                                fontSize: "13px"
                            }}
                        >
                            ■ Stop
                        </button>
                    )}
                </div>
            )}

            {/* Turtle canvas — shown only when code uses import turtle */}
            <div style={{
                display: load ? "block" : "none",
                position: "absolute", top: "40px", right: "8px",
                width: "95%", height: "500px",
                resize: "vertical", overflow: "auto",
                borderRadius: "6px", border: "1px solid #444",
                background: "#fff", zIndex: 5,
            }}>
                <button style={{
                    color: "black", border: "none", background: "none",
                    position: "absolute", right: "0px", top: "0.5rem",
                    cursor: "pointer", zIndex: 6,
                }} onClick={closingTurtle}>✕</button>
                <div id="turtle-container" style={{ width: "100%", height: "700px" }} />
            </div>

            {/* pygame-ce canvas container */}
<div
    id="pygame-container"
    style={{
        visibility: pygameVisible ? "visible" : "hidden",
        position: "absolute", top: "40px", right: "8px",
         width: "95%", height: "500px",

        resize: "vertical", overflow: "auto",
        borderRadius: "6px", border: "1px solid #444",
        background: "#000", zIndex: 5,
        pointerEvents: "none", // container is ghostly — NEVER blocks mouse
        display: "flex", alignItems: "center", justifyContent: "center",
    }}
>
    {/* Close button — must explicitly catch pointer events */}
    <button
        style={{
            color: "white", border: "none", background: "none",
            position: "absolute", right: "4px", top: "0.5rem",
            cursor: "pointer", zIndex: 7, fontSize: "16px",
            pointerEvents: "auto",
        }}
        onMouseDown={(e) => e.stopPropagation()} // don't trigger canvas focus
        onClick={closingPygame}
    >
        ✕
    </button>

    {pygameLoading && (
        <div style={{
            position: "absolute", top: 0, left: 0, width: "100%", height: "100%",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#4CAF50", fontFamily: "monospace", fontSize: "14px", zIndex: 6,
            pointerEvents: "none", // loading text shouldn't trap clicks
        }}>
            Loading pygame...
        </div>
    )}

    <canvas
        key={pygameRunKey}
        id="canvas"
        width={500}
        height={400}
        tabIndex={0}          // makes canvas focusable
        style={{
            width: "500px",
            height: "400px",
            display: "block",
            pointerEvents: "auto",
            outline: "none",  // remove ugly blue focus ring
             boxShadow: pygameFocused ? "0 0 0 3px #4CAF50" : "none",
        }}
    />
</div>

            {/* Output console */}
            <textarea
                id="outputConsole"
                className={styles.pythonoutput}
                readOnly
                value={output}
                placeholder="> output goes here..."
            />

            {/* Matplotlib plots — rendered below output console */}
            {plots.length > 0 && (
                <div style={{
                    position: "absolute",
                    bottom: "0",
                    right: "0",
                    width: "100%",
                    background: "#111",
                    zIndex: 10
                }}>
                    {plots.map((imgSrc, index) => (
                        <div key={index} style={{ position: "relative" }}>
                            <img
                                src={imgSrc}
                                alt={`Plot ${index + 1}`}
                                style={{
                                    maxWidth: "100%",
                                    borderRadius: "6px",
                                    border: "1px solid #444",
                                    background: "white",
                                    display: "block"
                                }}
                            />
                            {plots.length > 1 && (
                                <div style={{
                                    position: "absolute",
                                    top: "6px",
                                    left: "6px",
                                    background: "rgba(0,0,0,0.5)",
                                    color: "white",
                                    fontSize: "11px",
                                    padding: "2px 6px",
                                    borderRadius: "4px"
                                }}>
                                    Plot {index + 1}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </>
    );
}